import { Check, CircleHelp, Play, RotateCcw, SkipForward, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { REPLAYABLE_SECTIONS, SECTIONS, findPage, getSection } from './sections';
import { useHostTourStore } from './store';
import { TOUR, tourAttr } from './targets';
import type { SectionId, SectionStatus } from './types';

function statusLabel(status: SectionStatus, inProgress: boolean): string {
  if (inProgress) return 'In progress';
  if (status === 'done') return 'Completed';
  if (status === 'skipped') return 'Skipped';
  return 'Not started';
}

/**
 * Entry point for the walkthrough: sits in the host tab bar (and on the host
 * form headers). Lists every section with its status and offers Replay,
 * Resume, Replay all, and Skip the rest.
 */
export function HostTourGuideButton() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const mode = useHostTourStore((s) => s.mode);
  const sections = useHostTourStore((s) => s.sections);
  const activeRun = useHostTourStore((s) => s.activeRun);
  const paused = useHostTourStore((s) => s.paused);
  const hasOpenedGuide = useHostTourStore((s) => s.hasOpenedGuide);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!panelRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = () => {
    if (!open) useHostTourStore.getState().markGuideOpened();
    setOpen((v) => !v);
  };

  const replaySection = (id: SectionId) => {
    setOpen(false);
    const section = getSection(id);
    const st = useHostTourStore.getState();
    // A pending section in guided mode starts as part of the guide (closing pauses it);
    // anything else is a replay that simply ends when closed.
    const kind = st.mode === 'guided' && st.sections[id] === 'pending' ? 'guided' : 'replay';
    st.startRun(id, 0, kind);
    const firstPage = section.pages[0];
    if (!firstPage.matches(pathname)) {
      st.beginNavigation(firstPage.entry);
      navigate(firstPage.entry);
    }
  };

  const resume = () => {
    setOpen(false);
    const st = useHostTourStore.getState();
    const run = st.activeRun;
    st.resume();
    if (!run) return;
    const section = getSection(run.sectionId);
    if (findPage(section, pathname)) return;
    const step = section.steps[run.stepIndex];
    const page = section.pages.find((p) => p.key === step?.page) ?? section.pages[0];
    st.beginNavigation(page.entry);
    navigate(page.entry);
  };

  const replayAll = () => {
    setOpen(false);
    const st = useHostTourStore.getState();
    st.replayAll();
    st.startRun(SECTIONS[0].id, 0, 'guided');
    const first = SECTIONS[0].pages[0];
    if (!first.matches(pathname)) {
      st.beginNavigation(first.entry);
      navigate(first.entry);
    }
  };

  const skipRest = () => {
    setOpen(false);
    useHostTourStore.getState().skipAll();
  };

  const showDot = !hasOpenedGuide && mode !== 'unknown';
  const canResume = !!activeRun && paused;
  const hasPending = REPLAYABLE_SECTIONS.some((s) => sections[s.id] === 'pending');
  const showSkipRest = mode === 'guided' && hasPending;

  return (
    <div className="relative flex items-center" ref={panelRef}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="dialog"
        className="relative inline-flex items-center gap-1.5 text-sm leading-5 font-normal whitespace-nowrap text-gray-500 hover:text-gray-700"
        {...tourAttr(TOUR.guideButton)}
      >
        <CircleHelp className="h-4 w-4" aria-hidden />
        Guide
        {showDot && (
          <span
            className="absolute -top-0.5 -right-1.5 h-2 w-2 rounded-full bg-orange-500"
            aria-label="New"
          />
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Dashboard guide"
          className="absolute top-full right-0 z-40 mt-2 max-h-[70vh] w-72 overflow-y-auto rounded-lg border border-gray-200 bg-white p-3 shadow-lg"
        >
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-900">Dashboard guide</p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label="Close guide menu"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="mb-3 text-xs text-gray-500">
            Step-by-step help for each page. Replay any section at any time.
          </p>

          <ul className="divide-y divide-gray-100">
            {REPLAYABLE_SECTIONS.map((section) => {
              const status = sections[section.id];
              const inProgress = activeRun?.sectionId === section.id;
              return (
                <li key={section.id} className="flex items-center justify-between gap-2 py-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm text-gray-800">
                      {section.title}
                      {section.optional && (
                        <span className="ml-1 text-xs text-gray-400">(optional)</span>
                      )}
                    </p>
                    <p className="flex items-center gap-1 text-xs text-gray-500">
                      {status === 'done' && !inProgress && (
                        <Check className="h-3 w-3 text-primary" aria-hidden />
                      )}
                      {status === 'skipped' && !inProgress && (
                        <SkipForward className="h-3 w-3 text-gray-400" aria-hidden />
                      )}
                      {statusLabel(status, inProgress)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => replaySection(section.id)}
                    className="inline-flex flex-shrink-0 items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                  >
                    <Play className="h-3 w-3" aria-hidden />
                    {status === 'pending' ? 'Start' : 'Replay'}
                  </button>
                </li>
              );
            })}
          </ul>

          <div className="mt-3 flex flex-wrap gap-2 pt-3">
            {canResume && (
              <button
                type="button"
                onClick={resume}
                className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-dark"
              >
                <Play className="h-3 w-3" aria-hidden />
                Resume
              </button>
            )}
            <button
              type="button"
              onClick={replayAll}
              className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
            >
              <RotateCcw className="h-3 w-3" aria-hidden />
              Replay all
            </button>
            {showSkipRest && (
              <button
                type="button"
                onClick={skipRest}
                className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-50 hover:text-gray-700"
              >
                <SkipForward className="h-3 w-3" aria-hidden />
                Skip the rest
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
